import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { io } from "socket.io-client";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import socketConfig from "../config/socket";
import CustomToast from "../components/notifications/CustomToast";

// Create Socket Context
const SocketContext = createContext(null);

// Custom hook to use socket context
export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within a SocketProvider");
  }
  return context;
};

// Socket Provider Component
export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState(
    Notification.permission
  );
  const [audioContext, setAudioContext] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [processedMessages, setProcessedMessages] = useState(new Set());

  // Navigation hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Preload audio and enable on user interaction
  const prepareAudio = useCallback(() => {
    try {
      console.log("🔊 Preparing audio for notifications...");

      // Create audio element and preload
      const audio = new Audio("/notif_message.mp3");
      audio.preload = "auto";
      audio.volume = 0.5;

      // Test if audio can be played
      audio.load();

      // Store reference for later use
      setAudioContext(audio);
      setAudioReady(true);

      console.log("🔊 Audio prepared successfully");
      return audio;
    } catch (error) {
      console.error("🔊 Error preparing audio:", error);
      return null;
    }
  }, []);

  // Enable audio on first user interaction
  const enableAudio = useCallback(() => {
    if (!audioReady) {
      console.log("🔊 Enabling audio on user interaction");
      prepareAudio();

      // Try to play silent audio to unlock browser audio policy
      const unlockAudio = new Audio();
      unlockAudio.src =
        "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAADACAAAA//uQRAAAAWkAAAAA//uQRAADbwAAAAEAAAAA//uQRAAAAWkAAAAA//uQRAAAAMAAAAAAgAAAGOcAADOg";
      unlockAudio.volume = 0;
      unlockAudio.play().catch(() => {
        console.log("🔊 Silent audio play failed (expected on some browsers)");
      });
    }
  }, [audioReady, prepareAudio]);
  const requestNotificationPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      try {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        return permission;
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        return "denied";
      }
    }
    return Notification.permission;
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      console.log("🔊 Attempting to play notification sound...");

      // Use preloaded audio if available, otherwise create new one
      let audio = audioContext;
      if (!audio || !audioReady) {
        console.log("🔊 Creating new audio instance");
        audio = new Audio("/notif_message.mp3");
        audio.volume = 0.5;
      } else {
        console.log("🔊 Using preloaded audio");
        // Reset audio to beginning
        audio.currentTime = 0;
      }

      // Add event listeners for debugging
      audio.addEventListener("loadstart", () => {
        console.log("🔊 Audio loading started");
      });

      audio.addEventListener("canplay", () => {
        console.log("🔊 Audio can play");
      });

      audio.addEventListener("error", (e) => {
        console.error("🔊 Audio load error:", e);
      });

      // Attempt to play
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log("🔊 Notification sound played successfully");
          })
          .catch((error) => {
            console.warn("🔊 Could not play notification sound:", error);

            // Handle specific autoplay restrictions
            if (error.name === "NotAllowedError") {
              console.log(
                "🔊 Autoplay blocked - user interaction required first"
              );
              // Don't show toast for autoplay restrictions to avoid spam
              // User can enable audio by clicking anywhere on the page
            } else if (error.name === "NotSupportedError") {
              console.log("🔊 Audio format not supported");
            }
          });
      }
    } catch (error) {
      console.error("🔊 Error playing notification sound:", error);
    }
  }, [audioContext, audioReady]);

  const showDesktopNotification = useCallback(
    (data) => {
      if (notificationPermission === "granted" && "Notification" in window) {
        try {
          // Handle nested data structure from socket events
          const messageData = data.data || data;
          const customerName =
            messageData.customerName || // ✅ Backend kirim camelCase
            messageData.customer_name || // Fallback snake_case
            messageData.customer?.name ||
            messageData.sender?.name ||
            messageData.sender_name ||
            data.customerName || // Direct data customerName
            data.customer_name ||
            data.customer?.name ||
            data.sender?.name ||
            data.sender_name ||
            "Customer";

          // Get message content for preview
          const messageContent =
            messageData.message ||
            messageData.content ||
            messageData.body ||
            data.message ||
            data.content ||
            data.body ||
            "New message";

          const conversationId = data.conversation?.id || data.conversation_id;

          console.log("🖥️ Desktop notification for:", customerName);

          // Check if service worker is available for PWA notifications
          if (
            "serviceWorker" in navigator &&
            navigator.serviceWorker.controller
          ) {
            // Use PWA service worker notifications
            navigator.serviceWorker.ready
              .then((registration) => {
                const notificationOptions = {
                  body: `${customerName}: ${messageContent.substring(0, 100)}${
                    messageContent.length > 100 ? "..." : ""
                  }`,
                  icon: "/icons/icon-192x192.png",
                  badge: "/icons/icon-72x72.png",
                  tag: `chat-${conversationId || "message"}`,
                  requireInteraction: false,
                  silent: false,
                  timestamp: Date.now(),
                  data: {
                    conversationId: conversationId,
                    customerName: customerName,
                    url: conversationId
                      ? `/admin/chats/${conversationId}`
                      : "/admin/chats",
                  },
                  actions: [
                    {
                      action: "view",
                      title: "View Chat",
                      icon: "/icons/icon-72x72.png",
                    },
                  ],
                };

                registration
                  .showNotification("New Message", notificationOptions)
                  .then(() => {
                    console.log("🔔 PWA notification shown successfully");
                  })
                  .catch((error) => {
                    console.error("🔔 PWA notification error:", error);
                    // Fallback to regular notification
                    showFallbackNotification(customerName, messageContent);
                  });
              })
              .catch((error) => {
                console.error("🔔 Service worker not ready:", error);
                // Fallback to regular notification
                showFallbackNotification(customerName, messageContent);
              });
          } else {
            // Fallback to regular notification
            showFallbackNotification(customerName, messageContent);
          }
        } catch (error) {
          console.error("🖥️ Error showing desktop notification:", error);
        }
      } else {
        console.log("🖥️ Desktop notifications not available or not granted");
      }
    },
    [notificationPermission]
  );

  // Fallback notification function for non-PWA scenarios
  const showFallbackNotification = useCallback(
    (customerName, messageContent) => {
      const notification = new Notification("New Message", {
        body: `${customerName}: ${messageContent.substring(0, 100)}${
          messageContent.length > 100 ? "..." : ""
        }`,
        icon: "/icons/icon-192x192.png",
        tag: "new-message",
        requireInteraction: false,
      });

      // Close notification after 5 seconds
      setTimeout(() => notification.close(), 5000);

      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    },
    []
  );

  const showInAppToast = useCallback((data) => {
    // Handle nested data structure from socket events
    const messageData = data.data || data;
    const customerName =
      messageData.customerName || // ✅ Backend kirim camelCase
      messageData.customer_name || // Fallback snake_case
      messageData.customer?.name ||
      messageData.sender?.name ||
      messageData.sender_name ||
      data.customerName || // Direct data customerName
      data.customer_name ||
      data.customer?.name ||
      data.sender?.name ||
      data.sender_name ||
      "Customer";

    // Get message content
    const messageContent =
      messageData.message ||
      messageData.content ||
      messageData.body ||
      data.message ||
      data.content ||
      data.body ||
      "New message";

    // Get channel type
    const channelType =
      messageData.channelType ||
      messageData.channel_type ||
      data.channelType ||
      data.channel_type ||
      "whatsapp";

    // Get timestamp
    const timestamp =
      messageData.timestamp ||
      messageData.created_at ||
      data.timestamp ||
      data.created_at ||
      new Date().toISOString();

    console.log("🍞 Custom toast notification for:", customerName);
    console.log("🍞 Message preview:", messageContent.substring(0, 50));

    // Navigation handler for toast click
    const handleToastNavigate = () => {
      const conversationId = data.conversation?.id || data.conversation_id;
      console.log("🍞 Navigation handler called");
      console.log("🍞 ConversationId from data:", conversationId);
      console.log(
        "🍞 Data structure:",
        JSON.stringify(
          {
            conversation: data.conversation,
            conversation_id: data.conversation_id,
          },
          null,
          2
        )
      );

      if (conversationId) {
        console.log("🍞 Navigating to conversation:", conversationId);
        navigate(`/admin/chats/${conversationId}`);
        toast.dismiss(); // Close all toasts after navigation
      } else {
        console.log("🍞 Cannot navigate - no conversationId found");
      }
    };

    // Use custom WhatsApp-style toast with proper click handling
    toast.custom(
      (t) => (
        <CustomToast
          customerName={customerName}
          message={messageContent}
          channelType={channelType}
          timestamp={timestamp}
          conversationId={data.conversation?.id || data.conversation_id}
          onNavigate={handleToastNavigate}
          onClose={() => toast.dismiss(t.id)}
          toastId={t.id}
        />
      ),
      {
        duration: 5000,
        position: "top-right",
        style: {
          background: "transparent",
          boxShadow: "none",
          padding: 0,
        },
      }
    );
  }, []);

  const updateTabTitle = useCallback((count) => {
    const baseTitle = "Chat App";
    if (count > 0) {
      document.title = `(${count}) New Messages - ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, []);

  // 🔔 PWA Badge management for app icon
  const updateAppBadge = useCallback((count) => {
    if ("navigator" in window && "setAppBadge" in navigator) {
      try {
        if (count > 0) {
          navigator.setAppBadge(count);
          console.log(`🔔 App badge updated: ${count}`);
        } else {
          navigator.clearAppBadge();
          console.log("🔔 App badge cleared");
        }
      } catch (error) {
        console.warn("🔔 App badge API not supported:", error);
      }
    } else {
      console.log("🔔 App badge API not available");
    }
  }, []);

  // 🔊 Helper function to determine if sound should play based on conversation status
  const shouldPlaySoundForStatus = useCallback((conversationStatus) => {
    const allowedStatuses = ["escalated", "assigned"];
    const shouldPlay = allowedStatuses.includes(conversationStatus);

    console.log(`🔊 Sound filter check:`);
    console.log(`  - Conversation status: "${conversationStatus}"`);
    console.log(`  - Allowed statuses: [${allowedStatuses.join(", ")}]`);
    console.log(`  - Should play sound: ${shouldPlay}`);

    return shouldPlay;
  }, []);

  const handleNewMessage = useCallback(
    (data) => {
      // 🔍 ENHANCED LOGGING FOR SOCKET DATA ANALYSIS
      console.group("📨 NEW MESSAGE EVENT ANALYSIS");
      console.log("💬 Raw message received:", data);
      console.log("🔍 Message sender_type:", data.sender_type);
      console.log("🔍 Full data structure:", JSON.stringify(data, null, 2));

      // 🎯 CHECK FOR CONVERSATION STATUS FIELDS
      console.log("🔍 Conversation Status Analysis:");
      console.log("  - data.status:", data.status);
      console.log("  - data.conversation_status:", data.conversation_status);
      console.log("  - data.conversationStatus:", data.conversationStatus);
      console.log("  - data.data?.status:", data.data?.status);
      console.log(
        "  - data.data?.conversation_status:",
        data.data?.conversation_status
      );
      console.log(
        "  - data.data?.conversationStatus:",
        data.data?.conversationStatus
      ); // 🎯 NEW: Correct path

      // 🎯 CHECK FOR CONVERSATION INFO
      console.log("🔍 Conversation Info:");
      console.log("  - data.conversationId:", data.conversationId);
      console.log("  - data.conversation_id:", data.conversation_id);
      console.log("  - data.channelId:", data.channelId);
      console.log("  - data.channel_id:", data.channel_id);

      // 🎯 CHECK ALL TOP-LEVEL FIELDS
      console.log("🔍 All top-level fields:");
      Object.keys(data).forEach((key) => {
        console.log(`  - ${key}:`, data[key]);
      });

      // 🎯 CHECK NESTED DATA FIELDS (if exists)
      if (data.data && typeof data.data === "object") {
        console.log("🔍 Nested data fields:");
        Object.keys(data.data).forEach((key) => {
          console.log(`  - data.${key}:`, data.data[key]);
        });
      }

      // 🎯 SUMMARY FOR STATUS DETECTION
      const hasConversationStatus = !!(
        (
          data.status ||
          data.conversation_status ||
          data.conversationStatus ||
          data.data?.status ||
          data.data?.conversation_status ||
          data.data?.conversationStatus
        ) // 🎯 NEW: Correct path from backend
      );

      console.log(
        `%c🎯 CONVERSATION STATUS DETECTION RESULT:`,
        "background: #4CAF50; color: white; padding: 4px; border-radius: 4px;"
      );
      console.log(`  ✅ Has conversation status: ${hasConversationStatus}`);
      if (hasConversationStatus) {
        const statusValue =
          data.status ||
          data.conversation_status ||
          data.conversationStatus ||
          data.data?.status ||
          data.data?.conversation_status ||
          data.data?.conversationStatus;
        console.log(`  📊 Status value: "${statusValue}"`);
        console.log(
          `  🔊 Should play sound: ${shouldPlaySoundForStatus(statusValue)}`
        );
      } else {
        console.log(`  ❌ No conversation status found`);
      }

      console.groupEnd();

      // 🚫 PREVENT DUPLICATE NOTIFICATIONS
      const messageData = data.data || data;
      const messageId =
        messageData.messageId ||
        messageData.message_id ||
        messageData.id ||
        data.messageId ||
        data.message_id ||
        data.id;

      // Create unique identifier for this message
      const messageKey = `${messageId || "unknown"}_${
        messageData.timestamp || Date.now()
      }`;

      if (processedMessages.has(messageKey)) {
        console.log(
          "🚫 Duplicate message detected, skipping notification:",
          messageKey
        );
        return;
      }

      // Add to processed messages (keep only last 100 to prevent memory leak)
      setProcessedMessages((prev) => {
        const newSet = new Set(prev);
        newSet.add(messageKey);
        if (newSet.size > 100) {
          const firstKey = newSet.values().next().value;
          newSet.delete(firstKey);
        }
        return newSet;
      });

      // Check multiple possible sender type values for customer messages
      const isCustomerMessage =
        data.sender_type === "customer" ||
        data.sender_type === "user" ||
        data.sender_type === "client" ||
        (data.data &&
          (data.data.sender_type === "customer" ||
            data.data.sender_type === "user" ||
            data.data.sender_type === "client"));

      console.log("🔍 Is customer message:", isCustomerMessage);
      console.log("🔍 Message key:", messageKey);

      if (isCustomerMessage) {
        console.log(
          "✅ Customer message detected - checking conversation status for notifications"
        );

        // 🎯 EXTRACT CONVERSATION STATUS
        const conversationStatus =
          data.data?.conversationStatus ||
          data.data?.status ||
          data.conversationStatus ||
          data.status;

        console.log(
          `🔍 Extracted conversation status: "${conversationStatus}"`
        );

        // Update unread count (always for customer messages)
        const newCount = unreadCount + 1;
        setUnreadCount(newCount);
        console.log("📊 Updated unread count:", newCount);

        // 🔊 CONDITIONAL SOUND NOTIFICATION - Only for escalated/assigned
        if (
          conversationStatus &&
          shouldPlaySoundForStatus(conversationStatus)
        ) {
          console.log(
            `🔊 Playing notification sound for status: "${conversationStatus}"`
          );
          playNotificationSound();
        } else {
          console.log(
            `🔇 Skipping sound notification for status: "${conversationStatus}"`
          );
        }

        // 🍞 CONDITIONAL TOAST NOTIFICATION - Only for escalated/assigned
        if (
          conversationStatus &&
          shouldPlaySoundForStatus(conversationStatus)
        ) {
          console.log(
            `🍞 Showing toast notification for status: "${conversationStatus}"`
          );
          showInAppToast(data);
        } else {
          console.log(
            `🔇 Skipping toast notification for status: "${conversationStatus}"`
          );
        }

        // 🖥️ DESKTOP NOTIFICATION - Always for customer messages
        console.log("🖥️ Showing desktop notification...");
        showDesktopNotification(data);

        // 📑 TAB TITLE UPDATE - Always for customer messages
        console.log("📑 Updating tab title...");
        updateTabTitle(newCount);

        // 🔔 PWA BADGE UPDATE - Always for customer messages
        console.log("🔔 Updating app badge...");
        updateAppBadge(newCount);
      } else {
        // For non-customer messages (human agent, AI), just log
        console.log(
          `📝 Non-customer message from ${
            data.sender_type || data.data?.sender_type
          }:`,
          data.message || data.content || data.data?.message
        );
      }
    },
    [
      unreadCount,
      processedMessages,
      shouldPlaySoundForStatus,
      playNotificationSound,
      showDesktopNotification,
      showInAppToast,
      updateTabTitle,
      updateAppBadge,
    ]
  );

  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
    updateTabTitle(0);
    updateAppBadge(0);
  }, [updateTabTitle, updateAppBadge]);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, [requestNotificationPermission]);

  // Prepare audio on mount and user interaction
  useEffect(() => {
    // Prepare audio on component mount
    prepareAudio();

    // Add listeners for user interaction to unlock audio
    const handleUserInteraction = () => {
      enableAudio();
      // Remove listeners after first interaction
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [prepareAudio, enableAudio]);

  // Reset unread count when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      resetUnreadCount();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [resetUnreadCount]);

  // Handle PWA notification clicks
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === "NOTIFICATION_CLICK") {
        const { conversationId, customerName, url } = event.data;
        console.log(`🔔 PWA notification clicked:`, event.data);

        // Navigate to the conversation
        if (url) {
          console.log(`🔔 Navigating to: ${url}`);
          navigate(url);

          // Reset unread count when navigating
          resetUnreadCount();

          // Focus the window
          window.focus();
        }
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage
      );

      return () => {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage
        );
      };
    }
  }, [navigate, resetUnreadCount]);

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    try {
      setIsConnecting(true);
      setConnectionError(null);

      // Get fresh auth token
      const token = localStorage.getItem("token");

      // Create socket instance with config
      const newSocket = io(socketConfig.url, {
        ...socketConfig.options,
        auth: {
          token: token,
        },
      });

      // Connection event handlers
      newSocket.on(socketConfig.events.CONNECT, () => {
        console.log("✅ Socket connected");
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionError(null);
        setReconnectAttempts(0);
      });

      newSocket.on(socketConfig.events.DISCONNECT, (reason) => {
        console.log("❌ Socket disconnected:", reason);
        setIsConnected(false);
        setIsConnecting(false);

        // Handle different disconnect reasons
        if (reason === "io server disconnect") {
          // Server initiated disconnect, try to reconnect
          console.log("🔄 Server disconnected, attempting to reconnect...");
        }
      });

      newSocket.on(socketConfig.events.CONNECT_ERROR, (error) => {
        console.error("❌ Socket connection error:", error);
        setIsConnected(false);
        setIsConnecting(false);
        setConnectionError(error.message || "Connection failed");
      });

      newSocket.on(socketConfig.events.RECONNECT, (attemptNumber) => {
        console.log(`✅ Socket reconnected after ${attemptNumber} attempts`);
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionError(null);
        setReconnectAttempts(0);
      });

      newSocket.on(socketConfig.events.RECONNECT_ERROR, (error) => {
        console.error("❌ Socket reconnection error:", error);
        setReconnectAttempts((prev) => prev + 1);
      });

      // Handle app-update events (main event from backend)
      newSocket.on(socketConfig.events.APP_UPDATE, (data) => {
        // 🔍 ENHANCED LOGGING FOR ALL SOCKET EVENTS
        console.group("🚀 APP_UPDATE EVENT RECEIVED");
        console.log("📨 Raw app update:", data);
        console.log("🎯 Event type:", data.type);
        console.log("🔍 Full event structure:", JSON.stringify(data, null, 2));

        // 🎯 CONVERSATION STATUS FIELDS ANALYSIS
        if (data.type === socketConfig.events.NEW_MESSAGE) {
          console.log("📨 NEW_MESSAGE EVENT - Status Analysis:");
          console.log("  - Top level status fields:");
          console.log("    • data.status:", data.status);
          console.log(
            "    • data.conversation_status:",
            data.conversation_status
          );
          console.log(
            "    • data.conversationStatus:",
            data.conversationStatus
          );
          console.log("  - Nested data status fields:");
          console.log("    • data.data?.status:", data.data?.status);
          console.log(
            "    • data.data?.conversationStatus:",
            data.data?.conversationStatus
          );
          console.log("  - Conversation identifiers:");
          console.log("    • data.conversationId:", data.conversationId);
          console.log("    • data.conversation_id:", data.conversation_id);
          console.log("    • data.channelId:", data.channelId);
          console.log("    • data.channel_id:", data.channel_id);
        }
        console.groupEnd();

        if (socketConfig.debug) {
          console.log("📨 App update received:", data);
        }

        // Route events based on type
        switch (data.type) {
          case socketConfig.events.NEW_MESSAGE:
            handleNewMessage(data);
            break;
          case socketConfig.events.AI_RESPONSE:
            console.log("🤖 AI response:", data);
            break;
          case socketConfig.events.QR_CODE:
            console.log("📱 QR code update:", data);
            break;
          case socketConfig.events.WHATSAPP_CONNECTED:
            console.log("✅ WhatsApp connected:", data);
            break;
          case socketConfig.events.WHATSAPP_DISCONNECTED:
            console.log("❌ WhatsApp disconnected:", data);
            break;
          case socketConfig.events.SESSION_STATUS:
            console.log("📊 Session status:", data);
            break;
          default:
            console.log("❓ Unknown app update type:", data.type);
        }
      });

      setSocket(newSocket);

      return newSocket;
    } catch (error) {
      console.error("❌ Failed to initialize socket:", error);
      setIsConnecting(false);
      setConnectionError(error.message);
      return null;
    }
  }, []);

  // Disconnect socket
  const disconnectSocket = useCallback(() => {
    if (socket) {
      console.log("🔌 Disconnecting socket...");
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setIsConnecting(false);
      setConnectionError(null);
    }
  }, [socket]);

  // Emit event to server
  const emitEvent = useCallback(
    (event, data) => {
      if (socket && isConnected) {
        socket.emit(event, data);
        return true;
      } else {
        console.warn("⚠️ Cannot emit event: socket not connected");
        return false;
      }
    },
    [socket, isConnected]
  );

  // Listen to specific event
  const addEventListener = useCallback(
    (event, callback) => {
      if (socket) {
        socket.on(event, callback);
        return () => socket.off(event, callback); // Return cleanup function
      }
      return () => {}; // Return empty cleanup if no socket
    },
    [socket]
  );

  // Initialize socket on mount
  useEffect(() => {
    const newSocket = initializeSocket();

    // Cleanup on unmount
    return () => {
      if (newSocket) {
        console.log("🧹 Cleaning up socket connection...");
        newSocket.disconnect();
      }
    };
  }, []); // Empty dependency array - only run once

  // Context value
  const contextValue = {
    // Socket instance
    socket,

    // Connection state
    isConnected,
    isConnecting,
    connectionError,
    reconnectAttempts,
    unreadCount,
    notificationPermission,
    audioReady,

    // Actions
    initializeSocket,
    disconnectSocket,
    emitEvent,
    addEventListener,
    resetUnreadCount,
    requestNotificationPermission,
    enableAudio,
    playNotificationSound,

    // Config
    events: socketConfig.events,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
